<template>
  <v-container
    fluid
  >
    <v-row>
      <section-header
        :breadcrumb-items="breadcrumb"
        :button-title="$t('Assumptions.EditAssumptions.Title')"
        :button-icon="''"
        button
        button-to="EditAssumptions"
        :query="{from: activeTab, intra: interestRateSection}"
        :is-draft-status="isDraftStatus"
        :save-button-title="saveButtonTitle"
        @click-cancel="handleCancel"
        @click-save="handleSave"
      />
    </v-row>

    <v-row
      class="px-3"
    >
      <span
        v-if="niiSummaryData.Data.ValueDateFmt"
        class="portfolio-value"
      >
        {{ $t('Common.Date.Selector.Label') }} <span>{{ niiSummaryData.Data.ValueDateFmt }}</span>
      </span>
    </v-row>

    <v-row>
      <v-col cols="12">
        <nii-base />
      </v-col>
    </v-row>

    <!-- Save Assumptions Modal -->
    <save-assumptions-modal
      :open="showSaveModal"
      :on-close="closeSaveModal"
      :on-save="handleSaveAssumptions"
      :is-saving="isSavingAssumptions"
      :default-name="niiSummaryData.Data.AnalysisName || ''"
    />
  </v-container>
</template>

<script>

import i18nRouteMeta         from '@/mixins/i18n/i18nRouteMeta'
import SectionHeader         from '@/theme/default/components/common/SectionHeader'
import NiiBase from '@/theme/default/views/banking/risk/interest-rate/NII/NiiBase'
import API                   from '@/api/Api'
import { clone }             from '@/lib/utils/helper'
import NIISummaryResource from '@/api/resources/risks/NIISummaryResource'
import SaveAssumptionsModal from '@/theme/default/components/common/SaveAssumptionsModal.vue'

export default {
  name      : 'NII',
  components: {
    SaveAssumptionsModal,
    NiiBase,
    SectionHeader
  },
  directives: {},
  mixins    : [i18nRouteMeta],
  props     : {},
  dataStore : {
    niiSummaryData    : 'NII.FindSummaryData',
    selectedPeriod    : 'NII.SelectedPeriod',
    currentAnalysisId : 'ActiveAnalysis.currentAnalysisId',
    previousAnalysisId: 'ActiveAnalysis.previousAnalysisId',
    isDraftAnalysis   : 'ActiveAnalysis.isDraft'
  },
  enums    : {},
  dataModel: null,
  data () {
    return {
      originalApiData    : null,
      interestRateSection: 'NII',
      showSaveModal      : false,
      isSavingAssumptions: false
    }
  },
  computed: {
    isDraftStatus () {
      return this.isDraftAnalysis
    },
    saveButtonTitle () {
      return this.$t('Assumptions.EditAssumptions.Tabs.Button.SaveAssumptions')
    },
    breadcrumb () {
      return [
        {
          text    : this.$t('Route.Risks.Title'),
          disabled: true
        },
        {
          text    : this.$t('Route.InterestRate.Title'),
          disabled: true
        },
        {
          text    : this.niiSummaryData.Data.AnalysisName ?? 'Loading...',
          disabled: true
        },
        {
          text    : this.$t('NII.Title'),
          disabled: true
        }
      ]
    },
    accountId () {
      return this.user?.selectedAccountId || null
    },
    // Get activeTab from the route or provide a default
    activeTab () {
      return this.$route.params.tab || this.$t('NII.Tab.SynthesisRisk.Slug')
    }
  },
  created () {
    this.getData()
    this.$bus.$on('nii:period-changed', this.onPeriodChanged)
  },
  beforeDestroy () {
    this.$bus.$off('nii:period-changed', this.onPeriodChanged)
  },
  methods: {
    handleCancel () {
      this.currentAnalysisId = this.previousAnalysisId
      this.previousAnalysisId = null
      this.isDraftAnalysis = false
      this.$bus.$emit('nii:cancel-assumptions-edit', {})
      this.getData()
    },

    handleSave () {
      this.showSaveModal = true
    },

    closeSaveModal () {
      this.showSaveModal = false
    },

    async handleSaveAssumptions (assumptionName) {
      this.isSavingAssumptions = true

      try {
        const response = await API.Resource.InterestRate.SaveBankAnalysisAssumptions(
          this.accountId,
          this.currentAnalysisId,
          assumptionName
        )

        if (API.isResponseSuccess(response)) {
          this.showSaveModal = false

          // finish draft state
          this.previousAnalysisId = null

          // You can add a success message or redirect here
          // alert('Assumptions saved successfully! should redirect to saved items list when given')

          await this.$router.push({ name: 'PastAnalyses' })
        } else {
          const errors = API.responseErrors(response) || []
          this.$bus.$emit('app:error', errors)
        }
      } catch (error) {
        this.$bus.$emit('app:error', ['Failed to save assumptions'])
      } finally {
        this.isDraftAnalysis = false
        this.isSavingAssumptions = false
      }
    },

    getData () {
      API.Resource.NII.FindSummaryData(this.accountId, this.currentAnalysisId)
        .then(response => {
          this.handleResponse(response)
        })
        .catch(e => {
          this.handleResponse(e.response)
        })
    },

    handleResponse (response) {
      const data = API.responseData(response)?.findNiiAnalysis || null
      const errors = API.responseErrors(response) || []

      if (API.isResponseSuccess(response) && data) {
        if (!this.currentAnalysisId) {
          this.currentAnalysisId = data.niiAnalysisId
        }

        // Store the original API data for period switching
        this.originalApiData = clone(data)

        const o = clone(data)
        o.selectedPeriod = this.selectedPeriod

        const v = new NIISummaryResource(o)

        this.$set(this.niiSummaryData, 'Data', v)
      } else {
        this.$bus.$emit('app:error', errors)
      }
    },

    onPeriodChanged (newPeriod) {
      // Recreate the resource with the new selectedPeriod from original API data
      if (this.originalApiData) {
        const o = clone(this.originalApiData)
        o.selectedPeriod = newPeriod
        const v = new NIISummaryResource(o)
        this.$set(this.niiSummaryData, 'Data', v)
      }
    }
  }
}
</script>

<style scoped>
.portfolio-value {
  width:100%;
  margin-top: 4px;
  text-align: right;
  font-size: 12px;
  font-weight: 500;
  color:var(--v-synthesis-ui-grey-01-base);
  span {
    font-weight: 700;
  }
}

</style>
